import { Text } from '@chakra-ui/react';
import { PatchDataWrapper } from '@diamond/shared/data-access';
import {
  BackofficeActivitiesOrderItem,
  BackofficeActivitiesOrderItemWithVoucher,
  BackofficeOrderUpdate,
} from '@diamond/shared/types';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { NumericFormat } from 'react-number-format';

import { ORDER_CHANGELOG_QUERY_KEY } from '../../../api';
import {
  useUpdateActivitiesDetailColumn,
  useUpdateActivitiesDetailQueryData,
} from '../../../hooks';
import { EditableColumnProps } from '../../../types/activity-column-types';
import {
  formatCurrency,
  getOrderItemDisplayLatestPrice,
} from '../../../utils/activity-formatters';

const API_URL = 'backoffice/order';

interface Props
  extends EditableColumnProps<
    BackofficeActivitiesOrderItem,
    number | undefined
  > {
  queryKey?: QueryKey;
  activitiesId: string;
  token?: string;
  refetch: () => void;
  isVoucherEnabled?: boolean;
}

export function LatestPriceColumn({
  info,
  selectedData,
  onUpdate,
  queryKey = [],
  activitiesId,
  token,
  refetch,
  isVoucherEnabled = false,
}: Props) {
  const queryClient = useQueryClient();
  const item = info.row.original as BackofficeActivitiesOrderItemWithVoucher;
  const displayValue = getOrderItemDisplayLatestPrice(item, isVoucherEnabled);

  const { patchData } = PatchDataWrapper<BackofficeOrderUpdate>(
    API_URL,
    token as string,
    'admin'
  );

  const isSelected = selectedData.some(
    (row) => row.original.id === info.row.original.id
  );
  const canOverwritePrice = info.row.original.can_overwrite_price;

  const setQueryData = useUpdateActivitiesDetailQueryData({
    queryKey,
    info,
    name: 'latest_price',
  });

  const setValue = useUpdateActivitiesDetailColumn({
    onUpdate(value) {
      setQueryData(value);
      patchData(
        { latest_price: value },
        undefined,
        `${activitiesId}/item/${info.row.original.id}`
      ).then(async () => {
        refetch();
        await queryClient.invalidateQueries({
          queryKey: [ORDER_CHANGELOG_QUERY_KEY],
        });
      });
    },
  });

  if (isSelected && canOverwritePrice) {
    const handlePriceUpdate = (values: { floatValue: number | undefined }) => {
      setValue(String(values.floatValue ?? 0));
    };

    return (
      <div className="px-1">
        <NumericFormat
          key={`price-${info.row.original.id}-${displayValue}`}
          prefix="Rp"
          thousandSeparator="."
          decimalSeparator=","
          style={{
            borderColor: 'gray',
            borderWidth: 1,
            borderRadius: 4,
            padding: 4,
          }}
          allowNegative={false}
          defaultValue={displayValue || 0}
          onValueChange={handlePriceUpdate}
        />
        <Text className="text-xs" fontSize={12} mt="4px" color="red.500">
          *Input harga final
        </Text>
      </div>
    );
  }

  return (
    <Text fontSize="sm" fontWeight="400" color="#373F41">
      {formatCurrency(displayValue || 0)}
    </Text>
  );
}
